import React, { useCallback, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Blockquote from "@tiptap/extension-blockquote";
import HardBreak from '@tiptap/extension-hard-break' 
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo2,
  Redo2,
  Link as LinkIcon,
  Search,
  Plus,
} from "lucide-react";

const RichTextEditor = ({ value, onChange }) => {
  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const textColorRef = useRef(null);
  const bgColorRef = useRef(null);


  const CustomHardBreak = HardBreak.extend({
  parseHTML() {
    return [
      { tag: 'br' },
    ]
  },
  // This ensures that when rendering out to HTML, it drops clean code
  renderHTML({ HTMLAttributes }) {
    return ['br', HTMLAttributes]
  },
})


  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [3, 4, 5, 6],
        },
      }),
      Underline,
      TextStyle,
      // CustomHardBreak,
      HardBreak,
      Color,
      BulletList,
      OrderedList,
      ListItem,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline font-medium cursor-pointer",
        },
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: "border-l-4 border-gray-300 pl-4 italic my-4",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: "Write something amazing...",
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleFindReplace = () => {
    if (!findText) return;
    const currentHtml = editor.getHTML();
    const newHtml = currentHtml.split(findText).join(replaceText);
    editor.commands.setContent(newHtml);
    setShowFind(false);
  };

  // Focus the editor when clicking anywhere inside the content region
  const handleContainerClick = (e) => {
    if (editor && !editor.isFocused) {
      editor.chain().focus().run();
    }
  };

  const getActiveHeadingValue = () => {
    if (editor.isActive("heading", { level: 3 })) return "3";
    if (editor.isActive("heading", { level: 4 })) return "4";
    if (editor.isActive("heading", { level: 5 })) return "5";
    if (editor.isActive("heading", { level: 6 })) return "6";
    return "p";
  };

  if (!editor) return null;

  return (
    <div className="relative border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200 select-none">
        <div className="flex items-center gap-1 pr-2 mr-2 border-r border-slate-200">
          <select
            className="h-8 bg-white border border-slate-200 rounded-md px-2 text-xs font-semibold focus:outline-none"
            value={getActiveHeadingValue()}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "p") {
                editor.chain().focus().setParagraph().run();
              } else {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: parseInt(val, 10) })
                  .run();
              }
            }}
          >
            <option value="p">Paragraph</option>
            <option value="3">H3</option>
            <option value="4">H4</option>
            <option value="5">H5</option>
            <option value="6">H6</option>
          </select>
        </div>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive("bold")
              ? "bg-slate-200 text-primary"
              : "text-slate-600"
          }`}
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive("italic")
              ? "bg-slate-200 text-primary"
              : "text-slate-600"
          }`}
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive("underline")
              ? "bg-slate-200 text-primary"
              : "text-slate-600"
          }`}
        >
          <UnderlineIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
            editor.isActive("strike")
              ? "bg-slate-200 text-primary"
              : "text-slate-600"
          }`}
        >
          <Strikethrough size={16} />
        </button>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <div className="flex items-center gap-1 ml-2 relative">
          <button
            type="button"
            className="flex flex-col items-center justify-center w-8 h-8 text-sm border border-slate-200 rounded bg-white hover:bg-gray-100"
            title="Text Color"
            onClick={() => textColorRef.current?.click()}
          >
            <span>A</span>
            <span
              className="w-full h-[2px]"
              style={{
                background: editor.getAttributes("textStyle").color || "#000",
              }}
            />
          </button>

          <input
            ref={textColorRef}
            type="color"
            className="absolute opacity-0 pointer-events-none w-[1px] h-[1px]"
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
          />

          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded bg-white hover:bg-gray-100 text-sm"
            title="Background Color"
            onClick={() => bgColorRef.current?.click()}
          >
            🖍
          </button>

          <input
            ref={bgColorRef}
            type="color"
            className="absolute opacity-0 pointer-events-none w-[1px] h-[1px]"
            onChange={(e) =>
              editor
                .chain()
                .focus()
                .toggleHighlight({ color: e.target.value })
                .run()
            }
          />

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            className={`w-8 h-8 flex items-center justify-center border border-slate-200 rounded text-sm ${
              editor.isActive("superscript")
                ? "bg-gray-300"
                : "bg-white hover:bg-gray-100"
            }`}
            title="Superscript"
          >
            x²
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            className={`w-8 h-8 flex items-center justify-center border border-slate-200 rounded text-sm ${
              editor.isActive("subscript")
                ? "bg-gray-300"
                : "bg-white hover:bg-gray-100"
            }`}
            title="Subscript"
          >
            x₂
          </button>

          <button
            type="button"
            onClick={() =>
              editor.chain().focus().unsetAllMarks().clearNodes().run()
            }
            className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded bg-white hover:bg-gray-100 text-sm"
            title="Clear Formatting"
          >
            Tx
          </button>
        </div>

        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive({ textAlign: "left" })
              ? "bg-slate-200 text-primary"
              : ""
          }`}
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive({ textAlign: "center" })
              ? "bg-slate-200 text-primary"
              : ""
          }`}
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive({ textAlign: "right" })
              ? "bg-slate-200 text-primary"
              : ""
          }`}
        >
          <AlignRight size={16} />
        </button>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive("bulletList") ? "bg-slate-200 text-primary" : ""
          }`}
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive("orderedList") ? "bg-slate-200 text-primary" : ""
          }`}
        >
          <ListOrdered size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-1.5 rounded hover:bg-slate-200 ${
            editor.isActive("blockquote") ? "bg-slate-200 text-primary" : ""
          }`}
        >
          <Quote size={16} />
        </button>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={setLink}
            className={`p-1.5 rounded hover:bg-slate-200 ${
              editor.isActive("link") ? "bg-slate-200 text-primary" : ""
            }`}
          >
            <LinkIcon size={16} />
          </button>
          {editor.isActive("link") && (
            <button
              type="button"
              onClick={() => editor.chain().focus().unsetLink().run()}
              className="p-1.5 rounded hover:bg-slate-200 text-red-500"
              title="Remove link"
            >
              <Plus size={16} className="rotate-45" />
            </button>
          )}
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setShowFind(!showFind)}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
        >
          <Search size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30"
        >
          <Undo2 size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30"
        >
          <Redo2 size={16} />
        </button>
      </div>

      {/* Find/Replace Panel */}
      {showFind && (
        <div className="bg-white border-b border-slate-200 p-3 flex items-center gap-3 animate-in slide-in-from-top duration-200">
          <input
            type="text"
            placeholder="Find text..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={findText}
            onChange={(e) => setFindText(e.target.value)}
          />
          <input
            type="text"
            placeholder="Replace with..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
          />
          <button
            type="button"
            onClick={handleFindReplace}
            className="bg-primary text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-indigo-700 transition-colors"
          >
            Replace All
          </button>
          <button
            type="button"
            onClick={() => setShowFind(false)}
            className="p-1.5 text-slate-400 hover:text-slate-600"
          >
            <Plus className="w-4 h-4 rotate-45" />
          </button>
        </div>
      )}

      {/* Content Area - Clickable Container */}
      <div
        onClick={handleContainerClick}
        className="p-4 sm:p-6 min-h-[220px] cursor-text [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror]:outline-none"
      >
        <EditorContent
          editor={editor}
          className="prose prose-slate max-w-none"
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
